import React, { lazy, Suspense, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import CommentsList from 'app/components/CommonStyled/CommentsList';
import DraftBanner from 'app/components/CommonStyled/DraftBanner';
import { ResourceModalContext } from 'app/components/CommonStyled/ResourceModal/ResourceModalContext';
import { ResourceModalHandlerContext } from 'app/components/CommonStyled/ResourceModalHandler/ResourceModalHandlerContext';
import ShareBarShareCTA from 'app/components/CommonStyled/ShareBar/ShareBarShareCTA';
import CompanyResource from 'app/components/CompanyResource';
import { SkeletonItem } from 'app/components/DetailsPages/shared/styled';
import ResourceSubmission from 'app/components/ResourceSubmission';
import {
  CheckboxCheckIcon,
  CodeBrowserIcon,
  EmailIcon,
  ExpandChevronIcon,
  ExternalLinkIcon,
  File,
  FolderIcon,
  FullscreenIcon,
  GoogleSlidesIcon,
  OpenInNewIcon,
  PaywallIcon,
  PlayIcon,
  VimeoIcon,
  WorldGlobe,
  YoutubeIcon,
} from 'app/components/shared/icons';
import NotFoundPage from 'app/pages/NotFoundPage';
import { AnimatePresence } from 'framer-motion';
import useBehavioral from 'hooks/useBehavioral';
import useIsMobile from 'hooks/useIsMobile';
import useMetaTags from 'hooks/useMetaTags';
import { closeResourceModal } from 'store/reducers/global/global-reducer';
import { ThemeProvider, useTheme } from 'styled-components';

import { IResource, ResourceFormat } from '../ResourceCard';
import ResourcesDescription from '../ResourcesDescription';
import useResourceDetail from '../hooks/useResourceDetail';
import useResourceEmbedStatus from '../hooks/useResourceEmbedStatus';
import useResourcePreviewUrl from '../hooks/useResourcePreviewUrl';
import useResourceMessages from '../hooks/useResourceMessages';
import {
  requiresAuthenticatedResourcePreview,
  resourceDetailPath,
  resolveResourceViewBehavior,
} from '../resourceClickBehavior';
import ResourceContributors from './ResourceContributors';
import { ResourceDetailContext, ResourceDetailInfo } from './ResourceDetailContext';
import ResourceDetailExtraInformation from './ResourceDetailExtraInformation';
import ResourceDetailTeam from './ResourceDetailTeam';
import ResourceDetailVote from './ResourceDetailVote';
import ResourcesDetailInlineCTA from './ResourcesDetailInlineCTA';
import { resourceLongDescriptionTitle } from './resourceLongDescription';
import {
  ResourceCaseStudyDisclaimerWrapper,
  ResourceContainer,
  ResourceDetailTransitionViewport,
  ResourceCTAButton,
  ResourceDetailPageLink,
  ResourceCTAButtonContainer,
  ResourceCTAButtonPaywallContent,
  ResourceCTAButtonPaywallContentText,
  ResourceCTAButtonText,
  ResourceEmbedLoadingState,
  ResourceEmbedPreview,
  ResourceHeaderContainer,
  ResourceIframe,
  ResourceImage,
  ResourceImageContainer,
  ResourceImagePreviewContainer,
  ResourceImagePreviewThumbnail,
  ResourceImagePreviewThumbnailButton,
  ResourceImagePreviewThumbnailSelected,
  ResourceImagePreviewThumbnailLabel,
  ResourceImagePreviewThumbnailNotImage,
  ResourceImagePreviewWrapper,
  ResourceLongDescriptionContainer,
  ResourceOverallWrapper,
  ResourcePermissionsDisclaimerText,
  ResourcePermissionsDisclaimerTextContainer,
  ResourcePermissionsDisclaimerWrapper,
  ResourcesCommentsWrapper,
  ResourceShareBarContainer,
  ResourceSubmissionEditingWrapper,
  ResourceSubmissionStaticEditingWrapper,
  ResourceSubtitle,
  ResourcesWrapper,
  ResourceTagsChip,
  ResourceTagsContainer,
  ResourceTitle,
  ResourceTitleContainer,
  ResourceTitleHeadingContainer,
  ResourceTitleMessageAllContainer,
  ResourceTitleMessageAllText,
  ResourceTitleWrapper,
} from './styled';

const SideDrawer = lazy(() => import('app/components/CommonStyled/SideDrawer'));
const TickerTape = lazy(() => import('app/components/CommonStyled/TickerTape'));
const ShareBar = lazy(() => import('app/components/CommonStyled/ShareBar'));
const ContactDrawer = lazy(() => import('app/components/Drawers/Contact'));

interface ResourceDetailProps {
  slug?: string;
  onEmbedExpanded?: () => void;
  onEmbedCollapsed?: () => void;
  existingResource?: IResource;
  isFormPreview?: boolean;
  enableExistingResourceInteractions?: boolean;
  enableExistingResourceComments?: boolean;
  hideComments?: boolean;
  hideShareLink?: boolean;
  preventShowEditing?: boolean;
  renderEditing?: (onBack: () => void) => React.ReactNode;
  voteAction?: React.ReactNode;
}

interface ResourceDetailParams {
  slug?: string;
}

export default function ResourceDetail({
  slug,
  onEmbedExpanded,
  onEmbedCollapsed,
  existingResource = null,
  isFormPreview = false,
  enableExistingResourceInteractions = false,
  enableExistingResourceComments = false,
  hideComments = false,
  hideShareLink = false,
  preventShowEditing = false,
  renderEditing,
  voteAction,
}: ResourceDetailProps) {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const theme = useTheme() as { isVoting?: boolean } | undefined;
  const queryClient = useQueryClient();
  const resourceModalContext = useContext(ResourceModalHandlerContext);
  const invalidateOnUnmount = useRef<boolean>(false);
  const finalSlug = slug || useParams<ResourceDetailParams>().slug;
  const [showContactDrawer, setShowContactDrawer] = useState<boolean>(false);
  const [contactDrawerUuids, setContactDrawerUuids] = useState<string[]>([]);
  const { showWarning, setShowWarning, isEditing, setIsEditing } = useContext(ResourceModalContext);
  const needsAuthenticatedPreview =
    !!existingResource && !isFormPreview && requiresAuthenticatedResourcePreview(existingResource);
  const querySlug = existingResource?.slug || existingResource?.uuid || finalSlug;
  const { isLoading, isFetching, data, isError, refetch } = useResourceDetail(querySlug, {
    enabled: !!querySlug && (existingResource === null || needsAuthenticatedPreview),
    // Reopening a candidate must renew a possibly expired file-handler token.
    ...(needsAuthenticatedPreview ? { refetchOnMount: 'always' as const } : {}),
  });
  const finalResource = useMemo<IResource>(
    () => existingResource || data,
    [existingResource, data]
  );
  const resourceInteractionsEnabled =
    existingResource === null || enableExistingResourceInteractions;
  const resourceCommentsEnabled =
    !hideComments && (resourceInteractionsEnabled || enableExistingResourceComments);
  // A form preview must not borrow the slug of its surrounding route.
  const shareSlug = existingResource ? existingResource.slug : finalResource?.slug || finalSlug;
  const shareLink = hideShareLink
    ? undefined
    : resourceModalContext?.settings?.shareLink ||
      (shareSlug ? `${window.location.origin}${resourceDetailPath(shareSlug)}` : undefined);
  const { data: metaData } = useMetaTags(finalResource?.link, {
    enabled: !!finalResource?.link,
  });
  const {
    data: messages,
    isLoading: isMessagesLoading,
    isRefetching: isMessagesRefetching,
  } = useResourceMessages(finalResource?.uuid, {
    enabled: !!finalResource?.uuid && resourceCommentsEnabled,
  });

  const previewLoading =
    (needsAuthenticatedPreview || finalResource?.resource_format_name === ResourceFormat.PDF) &&
    (isLoading || isFetching);
  const previewError = needsAuthenticatedPreview && !previewLoading && (isError || !data?.link);
  // Only the authenticated media URL is live; Best Of content remains snapshot-owned.
  const previewResource = useMemo<IResource>(
    () =>
      needsAuthenticatedPreview
        ? { ...finalResource, link: previewError ? undefined : data?.link }
        : finalResource,
    [needsAuthenticatedPreview, finalResource, data?.link, previewError]
  );

  const [navHeight, setNavHeight] = useState<number>(0);
  const [onboardingStep, setOnboardingStep] = useState<number>(1);
  const [isEmbedExpanded, setIsEmbedExpanded] = useState<boolean>(false);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);

  const viewBehavior = useMemo(
    () => resolveResourceViewBehavior(previewResource || {}),
    [previewResource]
  );
  // An Embeddable URL whose site refuses framing leads with its cover instead of the error page.
  const { data: embedStatus } = useResourceEmbedStatus(querySlug, {
    enabled:
      !!querySlug &&
      previewResource?.resource_format_name === ResourceFormat.EMBEDDABLE_URL &&
      !!previewResource?.link &&
      !!previewResource?.image,
  });
  const embedBlocked = embedStatus === 'blocked';
  const shouldShowEmbed = viewBehavior.hasEmbeddableContent;

  const shouldShowOldInlineCTA = useMemo<boolean>(() => {
    if (
      finalResource?.resource_format_name === ResourceFormat.LINK &&
      !metaData?.img_url &&
      !finalResource?.image
    )
      return true;

    return false;
  }, [finalResource, metaData]);

  const initialValue = useMemo<ResourceDetailInfo>(() => {
    return {
      data: finalResource,
      onboardingStep,
      setOnboardingStep,
      hideLastStep: finalResource?.contributors?.people.length === 0,
      navHeight,
      setNavHeight,
      setIsEditing,
    };
  }, [onboardingStep, finalResource, navHeight, isEditing]);

  const handleFullScreen = async () => {
    if (needsAuthenticatedPreview) {
      const refreshed = await refetch();
      if (refreshed.isError || !refreshed.data?.link) return;
    }
    setIsFullScreen(true);
  };

  const handleEmbedExpanded = () => {
    setIsEmbedExpanded(true);
    if (onEmbedExpanded) return onEmbedExpanded();
  };

  const handleEmbedCollapsed = () => {
    setIsEmbedExpanded(false);
    if (onEmbedCollapsed) return onEmbedCollapsed();
  };

  const imageContainerVariants = {
    embedExpanded: {
      width: '100%',
      height: '65rem',
      maxHeight: '65rem',
      padding: '0px 0px',
    },
    embedCollapsed: {
      width: '50%',
      height: 'fit-content',
      maxHeight: 'unset',
    },
    mobileEmbedCollapsed: {
      width: '100%',
      height: 'fit-content',
      maxHeight: 'unset',
    },
  };

  const handleUpdateNotificationsStatus = (status: boolean) => {
    invalidateOnUnmount.current = true;
  };

  useEffect(() => {
    return () => {
      if (invalidateOnUnmount.current) {
        queryClient.invalidateQueries({ queryKey: [`resource-${finalSlug}-messages`] });
      }
    };
  }, []);

  const handleSubmissionSuccess = () => {
    dispatch(closeResourceModal({}));
  };

  const showExtraInformation = useMemo<boolean>(() => {
    if (!finalResource) return false;

    return (
      !!finalResource?.impact_metrics ||
      finalResource?.project_categories.length > 0 ||
      finalResource?.skills.length > 0 ||
      finalResource?.clients.length > 0
    );
  }, [finalResource]);

  const longDescriptionTitle = useMemo(
    () => resourceLongDescriptionTitle(finalResource),
    [finalResource?.long_description, finalResource?.type?.name]
  );

  const handleMessageAllClick = () => {
    setShowContactDrawer(true);
    setContactDrawerUuids(finalResource?.contributors?.people?.map((person) => person.uuid) || []);
  };

  if (isError && !existingResource)
    return (
      <NotFoundPage
        title="It seems like the page you were trying to access doesn't exist."
        subtitle="Mysterious!"
      />
    );

  if (isLoading && !existingResource) return <ResourceDetail.Skeleton />;
  if (!finalSlug && !existingResource) return null;

  const TransitionPresence = renderEditing ? React.Fragment : AnimatePresence;
  const EditingWrapper = renderEditing
    ? ResourceSubmissionStaticEditingWrapper
    : ResourceSubmissionEditingWrapper;

  return (
    <ThemeProvider
      theme={{
        navHeight,
        inDetailPage: !!slug,
        caseStudy: finalResource?.type?.name?.toLowerCase() === 'case studies',
      }}
    >
      <ResourceDetailContext.Provider value={initialValue}>
        <DraftBanner
          itemKey={finalResource?.uuid}
          draft={finalResource?.status === 'draft'}
          placement={slug || existingResource ? 'inline' : 'page'}
        />
        <ResourceDetailTransitionViewport
          $clipTransition={Boolean(renderEditing)}
          data-id="resource-detail-transition-viewport"
        >
          <TransitionPresence
            {...(!renderEditing ? { mode: 'popLayout' as const, initial: false } : {})}
          >
            {isEditing && (!preventShowEditing || Boolean(renderEditing)) ? (
              <EditingWrapper key="resource-submission-editing-wrapper">
                {renderEditing ? (
                  renderEditing(() => setIsEditing(false))
                ) : (
                  <ResourceSubmission
                    existingResource={finalResource}
                    onSubmissionSuccess={handleSubmissionSuccess}
                    onBack={() => setIsEditing(false)}
                  />
                )}
              </EditingWrapper>
            ) : (
              <ResourceOverallWrapper
                key="resource-detail-overall-wrapper"
                data-id="resource-detail-overall-wrapper"
                initial={renderEditing ? false : { x: 0 }}
                exit={{ x: '-100%' }}
                transition={renderEditing ? { duration: 0 } : undefined}
                animate={
                  isEmbedExpanded
                    ? {
                        gap: 0,
                      }
                    : {
                        gap: '55px',
                      }
                }
              >
                {!isMobile ? (
                  <ResourceImageComponent
                    isPreviewing={isFormPreview}
                    variants={imageContainerVariants}
                    initial={isEmbedExpanded ? 'embedExpanded' : 'embedCollapsed'}
                    animate={isEmbedExpanded ? 'embedExpanded' : 'embedCollapsed'}
                    showingEmbed={shouldShowEmbed || shouldShowOldInlineCTA}
                    resource={previewResource}
                    previewLoading={previewLoading}
                    previewError={previewError}
                    embedBlocked={embedBlocked}
                    onRetryPreview={() => refetch()}
                    isEmbedExpanded={isEmbedExpanded}
                    shouldShowOldInlineCTA={shouldShowOldInlineCTA}
                    metaData={metaData}
                    onEmbedExpanded={handleEmbedExpanded}
                    onEmbedCollapsed={handleEmbedCollapsed}
                    onFullScreen={handleFullScreen}
                  />
                ) : null}
                <AnimatePresence>
                  {!isEmbedExpanded ? (
                    <ResourcesWrapper
                      id="resource-details-wrapper"
                      initial={{
                        width: !isMobile ? '50%' : '100%',
                      }}
                    >
                      <Suspense fallback={<div style={{ height: '40px' }} />}>
                        <TickerTape context="resources" />
                      </Suspense>
                      <ResourceContainer>
                        <ResourceHeaderContainer>
                          {voteAction !== undefined ? voteAction : <ResourceDetailVote />}
                          <ResourceContributors
                            resource={finalResource}
                            preventShowEditing={preventShowEditing}
                          />
                          <ResourceTitleWrapper>
                            <ResourceTitleHeadingContainer>
                              <ResourceTitle>{finalResource?.title}</ResourceTitle>
                              {slug && !existingResource ? (
                                <ResourceDetailPageLink
                                  href={resourceDetailPath(finalSlug)}
                                  aria-label="View Resource page"
                                  title="Resource Page"
                                >
                                  <OpenInNewIcon width={10} height={10} fill="#000000" />
                                </ResourceDetailPageLink>
                              ) : null}
                            </ResourceTitleHeadingContainer>
                            {shareLink ? (
                              <ResourceTitleContainer>
                                <ShareBarShareCTA
                                  hideOnboarding
                                  data={{
                                    name: finalResource?.title,
                                    type: 'resource',
                                    tags: finalResource?.tags,
                                  }}
                                  shareLink={shareLink}
                                />
                              </ResourceTitleContainer>
                            ) : null}
                          </ResourceTitleWrapper>
                        </ResourceHeaderContainer>
                        {longDescriptionTitle ? (
                          <>
                            <ResourceSubtitle>{longDescriptionTitle}</ResourceSubtitle>
                            <ResourceLongDescriptionContainer>
                              <ResourcesDescription resource={finalResource} />
                            </ResourceLongDescriptionContainer>
                          </>
                        ) : null}
                        {showExtraInformation ? <ResourceDetailExtraInformation /> : null}
                        {finalResource?.contributors?.people?.length > 0 ? (
                          <ResourceDetailTeam />
                        ) : null}
                      </ResourceContainer>
                      {isMobile ? (
                        <ResourceImageComponent
                          isPreviewing={isFormPreview}
                          variants={imageContainerVariants}
                          initial={isEmbedExpanded ? 'embedExpanded' : 'mobileEmbedCollapsed'}
                          animate={isEmbedExpanded ? 'embedExpanded' : 'mobileEmbedCollapsed'}
                          showingEmbed={shouldShowEmbed || shouldShowOldInlineCTA}
                          resource={previewResource}
                          previewLoading={previewLoading}
                          previewError={previewError}
                          embedBlocked={embedBlocked}
                          onRetryPreview={() => refetch()}
                          isEmbedExpanded={isEmbedExpanded}
                          shouldShowOldInlineCTA={shouldShowOldInlineCTA}
                          metaData={metaData}
                          onEmbedExpanded={handleEmbedExpanded}
                          onEmbedCollapsed={handleEmbedCollapsed}
                          onFullScreen={handleFullScreen}
                        />
                      ) : null}
                      <ResourceShareBarContainer>
                        {resourceInteractionsEnabled ? (
                          <Suspense fallback={<div style={{ height: '50px' }} />}>
                            <ShareBar
                              type="resource"
                              slug={finalResource.uuid}
                              hideOnboarding
                              data={{
                                name: finalResource?.title,
                                type: 'resource',
                                tags: finalResource?.tags,
                              }}
                              messages={resourceCommentsEnabled ? messages?.messages : undefined}
                              shareLink={shareLink}
                              hideShare={!shareLink}
                              hideLike={Boolean(theme?.isVoting)}
                              showInformation
                            >
                              {finalResource?.contributors?.people?.length > 0 ? (
                                <ResourceTitleMessageAllContainer onClick={handleMessageAllClick}>
                                  <EmailIcon width={24} height={24} fill="var(--color-text)" />
                                  <ResourceTitleMessageAllText>
                                    Message All
                                  </ResourceTitleMessageAllText>
                                </ResourceTitleMessageAllContainer>
                              ) : null}
                            </ShareBar>
                          </Suspense>
                        ) : null}
                      </ResourceShareBarContainer>
                      {!isMessagesLoading && !isMessagesRefetching && resourceCommentsEnabled ? (
                        <ResourcesCommentsWrapper>
                          <CommentsList
                            id={finalResource?.uuid}
                            messages={messages?.messages}
                            messageable_type="resource"
                            messageable_uuid_or_slug={finalResource?.uuid}
                            notifications_status={messages?.notifications_status}
                            onUpdateNotificationsStatus={handleUpdateNotificationsStatus}
                          />
                        </ResourcesCommentsWrapper>
                      ) : null}
                    </ResourcesWrapper>
                  ) : isMobile ? (
                    <ResourceImageComponent
                      isPreviewing={isFormPreview}
                      variants={imageContainerVariants}
                      initial={isEmbedExpanded ? 'embedExpanded' : 'embedCollapsed'}
                      animate={isEmbedExpanded ? 'embedExpanded' : 'embedCollapsed'}
                      showingEmbed={shouldShowEmbed || shouldShowOldInlineCTA}
                      resource={previewResource}
                      previewLoading={previewLoading}
                      previewError={previewError}
                      embedBlocked={embedBlocked}
                      onRetryPreview={() => refetch()}
                      isEmbedExpanded={isEmbedExpanded}
                      shouldShowOldInlineCTA={shouldShowOldInlineCTA}
                      metaData={metaData}
                      onEmbedExpanded={handleEmbedExpanded}
                      onEmbedCollapsed={handleEmbedCollapsed}
                      onFullScreen={handleFullScreen}
                    />
                  ) : null}
                </AnimatePresence>
              </ResourceOverallWrapper>
            )}
          </TransitionPresence>
        </ResourceDetailTransitionViewport>
      </ResourceDetailContext.Provider>
      {isFullScreen ? (
        <CompanyResource
          resourceSlug={finalResource.slug}
          existingResource={existingResource ? previewResource : undefined}
          onClose={() => setIsFullScreen(false)}
        />
      ) : null}
      {showContactDrawer ? (
        <Suspense fallback={<></>}>
          <SideDrawer
            closeDrawer={() => {
              setShowContactDrawer(false);
              setContactDrawerUuids([]);
            }}
            withClose
          >
            <ContactDrawer
              fullCriteriaUuids={contactDrawerUuids}
              selectedUuids={contactDrawerUuids}
              backToRecipient={() => {
                setShowContactDrawer(false);
                setContactDrawerUuids([]);
              }}
              initialStep={contactDrawerUuids.length === 1 ? 2 : 1}
            />
          </SideDrawer>
        </Suspense>
      ) : null}
    </ThemeProvider>
  );
}

export function ResourceImageComponent({
  variants,
  initial,
  animate,
  showingEmbed,
  resource,
  isEmbedExpanded,
  isPreviewing,
  shouldShowOldInlineCTA,
  metaData,
  onEmbedExpanded,
  onEmbedCollapsed,
  onFullScreen,
  previewLoading = false,
  previewError = false,
  embedBlocked = false,
  onRetryPreview,
}: {
  variants: any;
  initial: any;
  animate: any;
  showingEmbed: boolean;
  resource: IResource;
  previewLoading?: boolean;
  previewError?: boolean;
  embedBlocked?: boolean;
  onRetryPreview?: () => void;
  isEmbedExpanded: boolean;
  isPreviewing: boolean;
  shouldShowOldInlineCTA: boolean;
  metaData: any;
  onEmbedExpanded: () => void;
  onEmbedCollapsed: () => void;
  onFullScreen: () => void;
}) {
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const images = useMemo<string[]>(() => {
    if (resource?.resource_format_name === ResourceFormat.IMAGE) {
      return [resource?.image, ...(resource?.additional_images || [])].filter(
        (image): image is string => !!image
      );
    }

    if (
      [
        ResourceFormat.VIMEO,
        ResourceFormat.PDF,
        ResourceFormat.GOOGLE_SLIDES,
        ResourceFormat.GOOGLE_STUDIO,
        ResourceFormat.READYMAG,
        ResourceFormat.EMBEDDABLE_URL,
        ResourceFormat.EMBEDDABLE_HTML,
      ].includes(resource?.resource_format_name as ResourceFormat)
    ) {
      return [
        resource?.link,
        ...[resource?.image, ...(resource?.additional_images || [])].filter(Boolean),
      ];
    }

    return [resource?.image, ...(resource?.additional_images || [])];
  }, [resource]);

  // Tile 0 is the embed; tile 1 is the cover.
  const leadWithCover =
    embedBlocked &&
    resource?.resource_format_name === ResourceFormat.EMBEDDABLE_URL &&
    !!resource?.image &&
    images.length > 1;

  useEffect(() => {
    setCurrentImageIndex(leadWithCover ? 1 : 0);
  }, [resource?.slug, images.length, leadWithCover]);

  const hasEmbeddedPreview =
    resource?.resource_format_name !== ResourceFormat.IMAGE &&
    resolveResourceViewBehavior(resource || {}).embeddable;
  const previewLabel =
    {
      [ResourceFormat.PDF]: 'PDF',
      [ResourceFormat.GOOGLE_SLIDES]: 'Slides',
      [ResourceFormat.VIMEO]: 'Video',
      [ResourceFormat.GOOGLE_STUDIO]: 'Report',
      [ResourceFormat.READYMAG]: 'Readymag',
      [ResourceFormat.EMBEDDABLE_HTML]: 'Interactive resource',
      [ResourceFormat.EMBEDDABLE_URL]: 'Web resource',
    }[resource?.resource_format_name] || 'Resource';

  const isYoutube = useMemo<boolean>(() => {
    return resource?.link?.includes('youtube.com');
  }, [resource]);

  const isVimeo = useMemo<boolean>(() => {
    return resource?.link?.includes('vimeo.com');
  }, [resource]);

  return (
    <ResourceImagePreviewWrapper
      variants={variants}
      initial={initial}
      animate={animate}
      showingEmbed={showingEmbed}
    >
      <ResourceImageContainer isEmbedExpanded={isEmbedExpanded}>
        <ResourceCaseStudyDisclaimerWrapper>
          <ResourcePermissionsDisclaimer resource={resource} />
          <ResourceCTA
            isPreviewing={isPreviewing}
            resource={resource}
            isEmbedExpanded={isEmbedExpanded}
            onEmbedExpanded={onEmbedExpanded}
            onEmbedCollapsed={onEmbedCollapsed}
            onFullScreen={onFullScreen}
          />
        </ResourceCaseStudyDisclaimerWrapper>
        <ResourceImagePreview
          previewLoading={previewLoading}
          previewError={previewError}
          onRetryPreview={onRetryPreview}
          resource={resource}
          isEmbedExpanded={isEmbedExpanded}
          shouldShowOldInlineCTA={shouldShowOldInlineCTA && currentImageIndex === 0}
          metaData={metaData}
          currentImageIndex={currentImageIndex}
          images={images}
        />
      </ResourceImageContainer>
      {images.length > 1 ? (
        <ResourceImagePreviewContainer role="group" aria-label="Resource previews">
          {images.map((image, index) => {
            const isResourcePreview = index === 0 && (hasEmbeddedPreview || shouldShowOldInlineCTA);
            const label = isResourcePreview
              ? previewLabel
              : 'Image ' + (index + (hasEmbeddedPreview || shouldShowOldInlineCTA ? 0 : 1));
            const selected = currentImageIndex === index;

            return (
              <ResourceImagePreviewThumbnailButton
                key={index}
                type="button"
                $active={selected}
                aria-label={'Show ' + label}
                aria-pressed={selected}
                data-id="resource-preview-thumbnail"
                onClick={() => setCurrentImageIndex(index)}
              >
                {isResourcePreview ? (
                  <ResourceImagePreviewThumbnailNotImage aria-hidden="true">
                    {resource.resource_format_name === ResourceFormat.GOOGLE_SLIDES ? (
                      <GoogleSlidesIcon width={60} height={60} />
                    ) : resource.resource_format_name === ResourceFormat.PDF ? (
                      <File width={36} height={36} />
                    ) : resource.resource_format_name === ResourceFormat.VIMEO ? (
                      isYoutube ? (
                        <YoutubeIcon width={36} height={36} />
                      ) : isVimeo ? (
                        <VimeoIcon width={36} height={36} />
                      ) : (
                        <PlayIcon width={36} height={36} />
                      )
                    ) : resource.resource_format_name === ResourceFormat.EMBEDDABLE_HTML ? (
                      <CodeBrowserIcon width={32} height={32} fill="var(--color-primary)" />
                    ) : shouldShowOldInlineCTA ? (
                      <FolderIcon width={36} height={36} />
                    ) : (
                      <WorldGlobe width={32} height={32} fill="var(--color-primary)" />
                    )}
                  </ResourceImagePreviewThumbnailNotImage>
                ) : (
                  <ResourceImagePreviewThumbnail
                    data-id="resource-image-thumbnail"
                    src={image}
                    alt=""
                    loading="lazy"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = '/images/resources/resource-fallback-image.png';
                    }}
                  />
                )}
                <ResourceImagePreviewThumbnailLabel>{label}</ResourceImagePreviewThumbnailLabel>
                {selected ? (
                  <ResourceImagePreviewThumbnailSelected aria-hidden="true">
                    <CheckboxCheckIcon width={12} height={9} fill="#ffffff" />
                  </ResourceImagePreviewThumbnailSelected>
                ) : null}
              </ResourceImagePreviewThumbnailButton>
            );
          })}
        </ResourceImagePreviewContainer>
      ) : null}
    </ResourceImagePreviewWrapper>
  );
}

export function ResourcePermissionsDisclaimer({ resource }: { resource: IResource }) {
  const privacyStatus = useMemo<string>(() => {
    if (resource?.internal_share_status === 'open') return 'Viewable by the Collective';
    if (resource?.internal_share_status === 'blinded') return 'Blinded details for the Collective';

    return '';
  }, [resource]);

  const clientShareStatus = useMemo<string>(() => {
    if (resource?.external_client_share_status === 'granted')
      return 'Permission to share externally';
    if (resource?.external_client_share_status === 'denied')
      return 'No permission to share externally';
    if (resource?.external_client_share_status === 'unknown') return 'Client permission pending';

    return '';
  }, [resource]);

  // Unset statuses have no label; render nothing rather than an empty box.
  if (!privacyStatus && !clientShareStatus) return null;

  return (
    <ResourcePermissionsDisclaimerWrapper>
      <ResourcePermissionsDisclaimerTextContainer>
        {privacyStatus ? (
          <ResourcePermissionsDisclaimerText>{privacyStatus}</ResourcePermissionsDisclaimerText>
        ) : null}
        {clientShareStatus ? (
          <ResourcePermissionsDisclaimerText>{clientShareStatus}</ResourcePermissionsDisclaimerText>
        ) : null}
      </ResourcePermissionsDisclaimerTextContainer>
    </ResourcePermissionsDisclaimerWrapper>
  );
}

interface ResourceCTAProps {
  resource: IResource;
  isPreviewing: boolean;
  defaultExpanded?: boolean;
  isEmbedExpanded?: boolean;
  onEmbedExpanded?: () => void;
  onEmbedCollapsed?: () => void;
  onFullScreen: () => void;
}

function ResourceCTA({
  resource,
  isPreviewing = false,
  isEmbedExpanded,
  onEmbedExpanded,
  onEmbedCollapsed,
  onFullScreen,
  defaultExpanded = false,
}: ResourceCTAProps) {
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);
  const resourceOpenFunction = useBehavioral('RESOURCE_OPENED', { resource_id: resource?.slug });
  const viewBehavior = resolveResourceViewBehavior(resource || {});

  const handleEmbedCollapsed = () => {
    setIsExpanded(false);
    if (onEmbedCollapsed) return onEmbedCollapsed();
  };

  const disabled = useMemo(() => {
    return isPreviewing && resource?.resource_format_name === ResourceFormat.PDF;
  }, [isPreviewing, resource?.resource_format_name]);

  const handleExpandClick = () => {
    if (disabled) return;
    if (isExpanded) {
      handleEmbedCollapsed();
      return;
    }

    resourceOpenFunction();
    setIsExpanded(true);
    if (onEmbedExpanded) return onEmbedExpanded();
  };

  const handleFullScreenClick = () => {
    resourceOpenFunction();
    onFullScreen();
  };

  const handleExternalLinkClick = () => {
    resourceOpenFunction();
    window.open(resource?.link, '_blank', 'noopener,noreferrer');
  };

  const showExternalLink = !viewBehavior.embeddable && !!resource?.link;
  if (!viewBehavior.canExpand && !showExternalLink) return <></>;
  return (
    <>
      <ResourceCTAButtonContainer>
        {viewBehavior.canExpand ? (
          <ResourceCTAButton
            onClick={handleExpandClick}
            disabled={disabled}
            disabledState={disabled}
          >
            <ResourceCTAButtonText>
              {disabled ? resource?.link_text || 'Open' : isEmbedExpanded ? 'Collapse' : 'Expand'}
            </ResourceCTAButtonText>
            {disabled ? (
              <ExternalLinkIcon width={21} height={21} fill="var(--color-primary)" />
            ) : (
              <ExpandChevronIcon />
            )}
            {resource?.has_paywall ? (
              <ResourceCTAButtonPaywallContent>
                <PaywallIcon width={10} height={10} />
                <ResourceCTAButtonPaywallContentText>
                  Potential Paywall
                </ResourceCTAButtonPaywallContentText>
              </ResourceCTAButtonPaywallContent>
            ) : null}
          </ResourceCTAButton>
        ) : null}
        {viewBehavior.canFullScreen && !isPreviewing ? (
          <ResourceCTAButton onClick={handleFullScreenClick} disabledState={false}>
            <ResourceCTAButtonText>Full Screen</ResourceCTAButtonText>
            <FullscreenIcon width={21} height={21} fill="var(--color-primary)" />
          </ResourceCTAButton>
        ) : null}
        {showExternalLink ? (
          <ResourceCTAButton onClick={handleExternalLinkClick} disabledState={false}>
            <ResourceCTAButtonText>{resource?.link_text || 'Open'}</ResourceCTAButtonText>
            <ExternalLinkIcon width={21} height={21} fill="var(--color-primary)" />
            {resource?.has_paywall ? (
              <ResourceCTAButtonPaywallContent>
                <PaywallIcon width={10} height={10} />
                <ResourceCTAButtonPaywallContentText>
                  Potential Paywall
                </ResourceCTAButtonPaywallContentText>
              </ResourceCTAButtonPaywallContent>
            ) : null}
          </ResourceCTAButton>
        ) : null}
      </ResourceCTAButtonContainer>
    </>
  );
}

function ResourceImagePreview({
  resource,
  isEmbedExpanded,
  shouldShowOldInlineCTA,
  metaData,
  currentImageIndex,
  images,
  previewLoading,
  previewError,
  onRetryPreview,
}: {
  resource: IResource;
  previewLoading: boolean;
  previewError: boolean;
  onRetryPreview?: () => void;
  isEmbedExpanded: boolean;
  shouldShowOldInlineCTA: boolean;
  metaData: any;
  currentImageIndex: number;
  images: string[];
}) {
  const [isEmbedLoading, setIsEmbedLoading] = useState<boolean>(true);
  const previewUrl = useResourcePreviewUrl(resource, {
    loading: previewLoading,
    error: previewError,
    active: currentImageIndex === 0,
  });

  useEffect(() => {
    setIsEmbedLoading(true);
  }, [
    resource?.slug,
    resource?.resource_format_name,
    previewUrl,
    resource?.content_html,
    currentImageIndex,
  ]);

  const shouldShowEmbed = useMemo<boolean>(() => {
    const viewBehavior = resolveResourceViewBehavior(resource || {});

    if (resource?.resource_format_name === ResourceFormat.IMAGE) {
      return !!images[currentImageIndex];
    }

    return viewBehavior.hasEmbeddableContent && currentImageIndex === 0;
  }, [resource, currentImageIndex, images]);

  if (currentImageIndex === 0 && (previewError || (previewLoading && !previewUrl))) {
    return (
      <ResourceEmbedPreview>
        <ResourceEmbedLoadingState role={previewError ? 'alert' : 'status'} aria-live="polite">
          {previewError ? (
            <div>
              <p>Resource preview could not be loaded.</p>
              <button type="button" onClick={onRetryPreview}>
                Try again
              </button>
            </div>
          ) : (
            'Loading resource…'
          )}
        </ResourceEmbedLoadingState>
      </ResourceEmbedPreview>
    );
  }

  if (resource?.resource_format_name === ResourceFormat.IMAGE && images.length === 0) {
    return null;
  }

  return (
    <>
      {shouldShowEmbed ? (
        <>
          {resource?.resource_format_name === ResourceFormat.IMAGE ? (
            <img
              data-id="resource-image-embed"
              src={images[currentImageIndex]}
              style={{
                width: '100%',
                flex: 1,
              }}
              alt={`${resource?.title}'s image`}
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = '/images/resources/resource-fallback-image.png';
              }}
            />
          ) : (
            <ResourceEmbedPreview>
              {isEmbedLoading ? (
                <ResourceEmbedLoadingState role="status" aria-live="polite">
                  Loading resource…
                </ResourceEmbedLoadingState>
              ) : null}
              <ResourceIframe
                title={resource.title}
                $whiteBackgroundFallback={
                  resource?.resource_format_name === ResourceFormat.EMBEDDABLE_HTML &&
                  !isEmbedLoading
                }
                src={
                  resource?.resource_format_name === ResourceFormat.EMBEDDABLE_HTML
                    ? undefined
                    : previewUrl
                }
                srcDoc={
                  resource?.resource_format_name === ResourceFormat.EMBEDDABLE_HTML
                    ? resource?.content_html
                    : undefined
                }
                sandbox={
                  resource?.resource_format_name === ResourceFormat.EMBEDDABLE_HTML
                    ? 'allow-scripts allow-modals allow-downloads allow-popups allow-popups-to-escape-sandbox'
                    : undefined
                }
                allowFullScreen
                onLoad={() => setIsEmbedLoading(false)}
                animate={
                  isEmbedExpanded
                    ? {
                        width: '100%',
                      }
                    : {
                        width: '100%',
                      }
                }
              />
            </ResourceEmbedPreview>
          )}
        </>
      ) : shouldShowOldInlineCTA ? (
        <ResourcesDetailInlineCTA resource={resource} />
      ) : (
        <ResourceImage
          data-id="resource-image"
          src={
            images[currentImageIndex] ||
            metaData?.img_url ||
            '/images/resources/resource-fallback-image.png'
          }
          alt={`${resource?.title}'s image`}
          onError={(e) => {
            e.currentTarget.src = `/images/resources/resource-fallback-image.png`;
          }}
        />
      )}
    </>
  );
}

interface ResourceTagsProps {
  resource: IResource;
}

export function ResourceTags({ resource }: ResourceTagsProps) {
  return (
    <ResourceTagsContainer>
      {resource.tags.map((tag, index) => (
        <ResourceTagsChip key={`${tag}-${index}`}>{tag}</ResourceTagsChip>
      ))}
    </ResourceTagsContainer>
  );
}

ResourceDetail.Skeleton = function ResourceDetailSkeleton() {
  const isMobile = useIsMobile();
  return (
    <ResourceOverallWrapper style={{ flexDirection: isMobile ? 'column' : 'row-reverse' }}>
      <ResourcesWrapper>
        <ResourceContributors.Skeleton />
        <SkeletonItem animated style={{ width: '25%', height: '20px', borderRadius: '8px' }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <SkeletonItem animated style={{ width: '100%', height: '20px', borderRadius: '8px' }} />
          <SkeletonItem animated style={{ width: '100%', height: '20px', borderRadius: '8px' }} />
          <SkeletonItem animated style={{ width: '100%', height: '20px', borderRadius: '8px' }} />
          <SkeletonItem animated style={{ width: '100%', height: '20px', borderRadius: '8px' }} />
          <SkeletonItem animated style={{ width: '100%', height: '20px', borderRadius: '8px' }} />
          <SkeletonItem animated style={{ width: '85%', height: '20px', borderRadius: '8px' }} />
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            borderTop: '1px solid #eaeaea',
            paddingTop: '6px',
          }}
        >
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}
          >
            <SkeletonItem
              animated
              style={{ width: '24px', height: '24px', borderRadius: '100%' }}
            />
            <SkeletonItem
              animated
              style={{ width: '100px', height: '20px', borderRadius: '8px' }}
            />
          </div>
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}
          >
            <SkeletonItem
              animated
              style={{ width: '24px', height: '24px', borderRadius: '100%' }}
            />
            <SkeletonItem
              animated
              style={{ width: '100px', height: '20px', borderRadius: '8px' }}
            />
          </div>
          <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}
          >
            <SkeletonItem
              animated
              style={{ width: '24px', height: '24px', borderRadius: '100%' }}
            />
            <SkeletonItem
              animated
              style={{ width: '100px', height: '20px', borderRadius: '8px' }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <SkeletonItem animated style={{ width: '25%', height: '20px', borderRadius: '8px' }} />
          <SkeletonItem animated style={{ width: '100%', height: '102px', borderRadius: '8px' }} />
          <SkeletonItem
            animated
            style={{ width: '103px', height: '48px', borderRadius: '100px', alignSelf: 'flex-end' }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <SkeletonItem animated style={{ width: '25%', height: '20px', borderRadius: '8px' }} />
          <SkeletonItem animated style={{ width: '100%', height: '158px', borderRadius: '8px' }} />
          <SkeletonItem animated style={{ width: '100%', height: '158px', borderRadius: '8px' }} />
          <SkeletonItem animated style={{ width: '100%', height: '158px', borderRadius: '8px' }} />
        </div>
      </ResourcesWrapper>
      <ResourceImageContainer style={{ width: isMobile ? '100%' : '50%' }} isEmbedExpanded={false}>
        <SkeletonItem animated style={{ width: '100%', height: '516px', borderRadius: '8px' }} />
      </ResourceImageContainer>
    </ResourceOverallWrapper>
  );
};
